import { useTranslation } from 'react-i18next';
import { TicketStatus, summariseChecklist } from '@nbh/shared';
import { api } from '../../lib/api';
import { ChecklistStepRow, type ChecklistStep } from './ChecklistStepRow';

export interface InteractiveChecklistProps {
  ticketId: string;
  status: TicketStatus;
  steps: ChecklistStep[];
  onChanged: () => void;
}

/**
 * Field execution of an SOP checklist (SPEC.md §5.4).
 *
 * Steps are only workable while the job is actually in hand. A closed or resolved
 * ticket keeps its checklist visible as the audit record of what was done, but
 * read-only — the record of a completed job must not be editable after sign-off.
 */
export function InteractiveChecklist({
  ticketId,
  status,
  steps,
  onChanged,
}: InteractiveChecklistProps) {
  const { t } = useTranslation();

  if (steps.length === 0) {
    return <p className="text-sm text-slate-500">{t('detail.noChecklist')}</p>;
  }

  const editable = status === TicketStatus.InProgress || status === TicketStatus.OnHold;
  const progress = summariseChecklist(steps);

  async function patch(stepId: string, body: Parameters<typeof api.updateChecklistStep>[2]) {
    await api.updateChecklistStep(ticketId, stepId, body);
    onChanged();
  }

  return (
    <div>
      <p className="mb-3 text-xs text-slate-400">
        {t('detail.checklistProgress', {
          completed: progress.completed,
          total: progress.total,
        })}
        {progress.missingProof > 0 &&
          ` · ${t('detail.missingProof', { count: progress.missingProof })}`}
      </p>

      {!editable && <p className="mb-3 text-xs text-slate-500">{t('checklist.readOnly')}</p>}

      <ul className="space-y-2">
        {steps.map((step) => (
          <ChecklistStepRow
            key={step.id}
            step={step}
            disabled={!editable}
            onToggle={(isCompleted) => patch(step.id, { isCompleted })}
            onAttachPhoto={(photoAttachmentId) => patch(step.id, { photoAttachmentId })}
            onSaveNotes={(notes) => patch(step.id, { notes })}
          />
        ))}
      </ul>
    </div>
  );
}
